import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { Separator } from '../../common/Separator';
import { Comment } from '@models/Comments/CommentModel';
import { CommentView } from './CommentView';
import { CommentList } from '@models/Comments/CommentListModel';
import type { ICommentListStore } from '../CommentList';
import {
  IReplyToCommentBarProps,
  ReplyToCommentBar,
} from './ReplyToCommentBar';
import { CommentReplies } from './CommentReplies';
import { CommentViewLeftSpace } from './CommentItemLeftSpace';
import { CommentInputModeEnum } from '../CommentInput';
import { User } from '@models/Viewer/UserModel';

const Container = styled(View)({});

const ListContainer = styled(View)({
  flex: 1,
});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

export interface ICommentItemProps {
  item: Comment;
  onPressReply?(item: Comment): void;
  onPressEdit?(item: Comment): void;
  commentListStore: ICommentListStore;
  commentInputSize?: IReplyToCommentBarProps['commentInputSize'];
  onPressUser?(user: User): void;
}

export const CommentItem = observer<ICommentItemProps>(
  ({
    item,
    onPressReply,
    onPressEdit,
    commentListStore,
    commentInputSize,
    onPressUser,
  }) => {
    const [isShowList, setIsShowList] = useState(false);

    const onPressReplyItem = useCallback((commentItem: Comment) => {
      if (onPressReply) {
        onPressReply(item);
      } else {
        commentListStore.setId(
          commentItem.id,
          CommentInputModeEnum.Create,
        );
      }
    }, []);

    const onPressEditItem = useCallback((commentItem: Comment) => {
      if (onPressEdit) {
        onPressEdit(commentItem);
      } else {
        commentListStore.setId(
          commentItem.id,
          CommentInputModeEnum.Update,
        );
      }
    }, []);

    function toggleIsShowList() {
      setIsShowList((v) => !v);
    }

    return (
      <Container>
        <CommentView
          item={item}
          onPressReply={onPressReplyItem}
          isChildrenListExtended={isShowList}
          onPressExtend={toggleIsShowList}
          onPressEdit={onPressEditItem}
          commentListStore={commentListStore}
          commentInputSize={commentInputSize}
          onPressUser={onPressUser}
        />

        <ReplyToCommentBar
          item={item}
          commentListStore={commentListStore}
          commentInputSize={commentInputSize}
        />

        {item.hasChildren && (
          <RowContainer>
            <CommentViewLeftSpace />

            {isShowList && (
              <ListContainer>
                <Separator size={['$5', '$6']} />
                <CommentReplies
                  list={item.comments as CommentList}
                  onPressReply={onPressReplyItem}
                  onPressEdit={onPressEditItem}
                  commentListStore={commentListStore}
                  commentInputSize={commentInputSize}
                  onPressUser={onPressUser}
                />
              </ListContainer>
            )}
          </RowContainer>
        )}
      </Container>
    );
  },
);
